// SMS API - API integration for SMS communications
// Uses ApiClient with dependency injection pattern

import type { ApiClient } from '@commander/api-client';
import type { MessagingConversation, SMSMessage } from '../types';
import { formatTenantContactName } from '../utils/formatters';

// Backend response types (snake_case from API)
interface BackendSMSThread {
  id: string;
  user_id: string;
  company_prospect_id: string;
  company_contact_id: string;
  tenant_contact_id: string | null;
  contact_e164_phone_number: string;
  contact_name: string | null;
  last_message_id: string | null;
  last_message_at: string | null;
  preview_body: string | null;
  preview_is_draft: boolean;
  preview_direction: 'incoming' | 'outgoing' | null;
  unread_count?: number; // Number of unread incoming messages in this thread
  created_at: string;
  updated_at: string;
  user?: {
    id: string;
    email: string;
    first_name?: string;
    last_name?: string;
  } | null;
  tenant_contact?: {
    id: string;
    first_name: string;
    last_name: string;
    email?: string;
  } | null;
}

interface BackendSMSMessage {
  id: string;
  sms_thread_id: string;
  sms_generated_message_id: string | null;
  external_id: string | null;
  from_number: string;
  to_number: string;
  body: string | null;
  direction: 'incoming' | 'outgoing';
  status: string;
  type: string;
  is_starred: boolean;
  is_read: boolean;
  media_urls: any[];
  deleted: boolean;
  created_at: string;
  updated_at: string;
}

// Request/response types
export interface SMSThreadParams {
  page?: number;
  limit?: number;
}

export interface CreateThreadData {
  tenant_contact_id?: string;
  phone_number?: string;
  contact_name?: string;
  body: string;
}

export interface SendMessageData {
  body: string;
}

// Response types for infinite query compatibility
export interface SMSThreadsResponse {
  threads: MessagingConversation[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface SMSThreadDetailsResponse {
  conversation: MessagingConversation;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Transform backend SMS thread response to frontend MessagingConversation type
 */
function transformSMSThread(
  backendThread: BackendSMSThread,
  messages?: BackendSMSMessage[]
): MessagingConversation {
  // Prioritize current tenant contact name (source of truth) over denormalized contact_name
  const contactName = backendThread.tenant_contact
    ? formatTenantContactName(backendThread.tenant_contact) ||
      backendThread.contact_name ||
      'Unknown Contact'
    : backendThread.contact_name || 'Unknown Contact';

  return {
    id: backendThread.id,
    ownerUserId: backendThread.user_id,
    contactId: backendThread.tenant_contact_id || backendThread.company_contact_id,
    contactName,
    phoneNumber: backendThread.contact_e164_phone_number,
    channel: 'sms' as const,
    lastMessageAt: backendThread.last_message_at
      ? new Date(backendThread.last_message_at)
      : new Date(),
    lastMessage: backendThread.preview_body || '',
    unreadCount: backendThread.unread_count || 0, // Use backend-calculated unread count
    messages: messages ? messages.map(transformSMSMessage) : [],
    isActive: true,
    createdAt: new Date(backendThread.created_at),
    updatedAt: new Date(backendThread.updated_at),
  };
}

/**
 * Transform backend SMS message response to frontend SMSMessage type
 */
function transformSMSMessage(backendMessage: BackendSMSMessage): SMSMessage {
  return {
    id: backendMessage.id,
    type: 'sms' as const,
    direction: backendMessage.direction,
    contactId: '', // Not provided in message response, set by parent
    contactName: '', // Not provided in message response, set by parent
    phoneNumber:
      backendMessage.direction === 'incoming'
        ? backendMessage.from_number
        : backendMessage.to_number,
    subject: 'SMS',
    content: backendMessage.body || '',
    message: backendMessage.body || '',
    segments: Math.ceil((backendMessage.body || '').length / 160),
    channel: 'sms' as const,
    conversationId: backendMessage.sms_thread_id,
    timestamp: new Date(backendMessage.created_at),
    status: backendMessage.status as any,
    mediaUrls: backendMessage.media_urls,
    metadata: {},
    createdBy: '', // Not provided in backend response
    createdAt: new Date(backendMessage.created_at),
    updatedAt: new Date(backendMessage.updated_at),
  };
}

/**
 * Factory function to create SMS API service with ApiClient dependency injection
 * @param apiClient - ApiClient instance from context
 * @returns Service object with all SMS API methods
 *
 * @example
 * ```ts
 * import { useApiClient } from '@commander/api-client';
 * import { createSmsApi } from './services/smsApi';
 *
 * const apiClient = useApiClient();
 * const smsApi = createSmsApi(apiClient);
 * const threads = await smsApi.getSMSThreads({ page: 1, limit: 20 });
 * ```
 */
export const createSmsApi = (apiClient: ApiClient) => ({
  /**
   * Get paginated list of SMS threads
   * GET /communications/sms_threads
   */
  async getSMSThreads(params?: SMSThreadParams): Promise<SMSThreadsResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());

    const endpoint = `/communications/sms_threads${queryParams.toString() ? `?${queryParams}` : ''}`;
    const response = await apiClient.get<{
      data: BackendSMSThread[];
      meta: {
        current_page: number;
        from: number;
        last_page: number;
        per_page: number;
        to: number;
        total: number;
      };
    }>(endpoint);

    return {
      threads: response.data.map((thread) => transformSMSThread(thread)),
      pagination: {
        page: response.meta.current_page,
        limit: response.meta.per_page,
        total: response.meta.total,
        totalPages: response.meta.last_page,
      },
    };
  },

  /**
   * Get SMS thread details with messages
   * GET /communications/sms_threads/:threadId
   */
  async getSMSThreadDetails(
    threadId: string,
    params?: SMSThreadParams
  ): Promise<SMSThreadDetailsResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());

    const endpoint = `/communications/sms_threads/${threadId}${queryParams.toString() ? `?${queryParams}` : ''}`;
    const response = await apiClient.get<{
      data: {
        thread: BackendSMSThread;
        messages: BackendSMSMessage[];
      };
      meta: {
        current_page: number;
        from: number;
        last_page: number;
        per_page: number;
        to: number;
        total: number;
      };
    }>(endpoint);

    // Backend returns messages in DESC order (newest first)
    // Reverse to display oldest → newest in chat UI
    const messagesOldestFirst = [...response.data.messages].reverse();

    return {
      conversation: transformSMSThread(response.data.thread, messagesOldestFirst),
      pagination: {
        page: response.meta.current_page,
        limit: response.meta.per_page,
        total: response.meta.total,
        totalPages: response.meta.last_page,
      },
    };
  },

  /**
   * Create a new SMS thread and send first message
   * POST /communications/sms_threads
   */
  async createThread(data: CreateThreadData) {
    const response = await apiClient.post<{
      data: {
        thread: BackendSMSThread;
        message: BackendSMSMessage;
      };
    }>('/communications/sms_threads', data);

    return {
      thread: transformSMSThread(response.data.thread),
      message: transformSMSMessage(response.data.message),
    };
  },

  /**
   * Send an SMS message in an existing thread
   * POST /communications/sms_threads/:threadId/sms_messages
   */
  async sendMessage(threadId: string, data: SendMessageData) {
    const response = await apiClient.post<{
      data: BackendSMSMessage;
    }>(`/communications/sms_threads/${threadId}/sms_messages`, data);

    return transformSMSMessage(response.data);
  },

  /**
   * Delete an SMS message (soft delete)
   * DELETE /communications/sms_threads/:threadId/sms_messages/:messageId
   */
  async deleteMessage(threadId: string, messageId: string): Promise<void> {
    await apiClient.delete<void>(
      `/communications/sms_threads/${threadId}/sms_messages/${messageId}`
    );
  },

  /**
   * Mark SMS message as read or unread
   * PATCH /communications/sms_threads/:threadId/sms_messages/:messageId/read
   */
  async markMessageRead(threadId: string, messageId: string, isRead: boolean) {
    const response = await apiClient.patch<{
      data: BackendSMSMessage;
    }>(`/communications/sms_threads/${threadId}/sms_messages/${messageId}/read`, {
      is_read: isRead,
    });

    return transformSMSMessage(response.data);
  },

  /**
   * Star or unstar an SMS message
   * PATCH /communications/sms_threads/:threadId/sms_messages/:messageId/star
   */
  async markMessageStar(threadId: string, messageId: string, isStarred: boolean) {
    const response = await apiClient.patch<{
      data: BackendSMSMessage;
    }>(`/communications/sms_threads/${threadId}/sms_messages/${messageId}/star`, {
      is_starred: isStarred,
    });

    return transformSMSMessage(response.data);
  },

  /**
   * Mark all incoming unread messages in a thread as read
   * PATCH /communications/sms_threads/:threadId/mark_read
   */
  async markThreadRead(threadId: string) {
    const response = await apiClient.patch<{
      data: {
        threadId: string;
        messagesMarkedRead: number;
      };
    }>(`/communications/sms_threads/${threadId}/mark_read`);

    return response.data;
  },
});

// Type for the service instance
export type SmsApi = ReturnType<typeof createSmsApi>;
